<template>
  <div>
    <fin-portfolio-value title="Deine Strategie"
                         hideValueLine
                         hideValueSubline>
      <template #contentPrepend>
        <div class="my-3"
             style="display: flex; align-items: center; position: relative;">
          <fin-archetype-avatar v-if="profileArchetype"
                                :archetype-id="profileArchetype.id"
                                :tooltip="profileArchetype.title"
                                class="mr-3"/>
          <p v-if="strategy" class="strategy-name mb-1">
            {{ strategy.name }} <br/>
            <span v-if="assetClass.id === 'SHARE' && shareUniverseName"
                  class="universum">
              Aktienuniversum:  {{ shareUniverseName }}
            </span>
          </p>
          <p v-else class="no-strategy">Du hast noch keine Strategie gewählt</p>

        </div>
      </template>
      <template #buttons>
        <div v-if="strategy">
          <v-btn outlined
                 @click="gotoStrategyDetails()"
                 :disabled="isInactivePortfolio">
            Details
          </v-btn>
          <v-btn text
                 @click="changeStrategy"
                 :disabled="isInactivePortfolio">
            Bearbeiten
          </v-btn>
        </div>
        <div v-else>
          <v-btn text
                 @click="showChooseStrategy"
                 :disabled="isInactivePortfolio">
            Strategie wählen
          </v-btn>
        </div>
      </template>
    </fin-portfolio-value>

    <fin-dialog v-model="showDialogChangeStrategy"
                :max-width="550">
      <template #title>
        Strategie ändern
      </template>
      <template>
        <p :class="'dialog__text mb-6 ' + $vuetify.breakpoint.xs ? '' : 'pr-12'">
          Du kannst entweder eine andere Strategie auswählen oder die Strategie löschen und ohne fortfahren.
        </p>
        <p v-if="strategy"><span style="font-weight: bold;">Deine aktuelle Strategie:</span><br/>{{ strategy.name }}</p>

        <v-container fluid>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-btn :class="$vuetify.breakpoint.xs ? '' : 'mr-4'"
                     dark
                     @click="showChooseStrategy"
                     :disabled="isInactivePortfolio">
                neue Strategie wählen
              </v-btn>
            </v-col>
            <v-col :align="$vuetify.breakpoint.smAndUp ? 'right' : ''" cols="12" sm="6">
              <v-btn class="px-0"
                     text
                     @click="showDeleteStrategyConfirmDialog"
                     :disabled="isInactivePortfolio">
                <v-icon class="mr-2"
                        color="fin-color-primary-dark">
                  icon-delete
                </v-icon>
                Strategie löschen
              </v-btn>
            </v-col>
          </v-row>
        </v-container>

        <div v-if="assetClass.id === 'SHARE'">
          <v-divider :class="$vuetify.breakpoint.xs ? 'mt-5 mb-4' : 'mt-9 mb-6'"/>

          <p>
            <fin-lexicon-text auto-solve
                              text="Du kannst hier ein anderes Aktienuniversum auswählen und die Aktienauswahl begrenzen oder erweitern."/>
          </p>
          <p v-if="shareUniverseName">
            <span style="font-weight: bold;">Dein aktuelles Aktienuniversum:</span><br/>{{ shareUniverseName }}
          </p>

          <v-container fluid>
            <v-row dense>
              <v-col cols="12"
                     sm="6">
                <v-btn :class="$vuetify.breakpoint.xs ? 'px-0' : ''"
                       text
                       @click="showChangeShareUniverseDialog = true"
                       :disabled="isInactivePortfolio">
                  Aktienuniversum ändern
                </v-btn>
              </v-col>
              <v-col :align="$vuetify.breakpoint.smAndUp ? 'right' : ''"
                     cols="12"
                     sm="6">
                <v-btn class="px-0"
                       text
                       @click="deleteShareUniverse"
                       :disabled="isInactivePortfolio">
                  <v-icon class="mr-2"
                          color="fin-color-primary-dark">
                    icon-delete
                  </v-icon>
                  Aktienuniversum löschen
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </template>
    </fin-dialog>

    <fin-choose-selector
      v-model="showChangeShareUniverseDialog"
      :newListCreationEnabled="false"
      :options="loadUniverse"
      :preSelectedItem="selectedUniverse"
      labelHint="Wähle ein Aktienuniversum"
      ok-button-label="Anwenden"
      select-box-item-value="id"
      selectBoxItemTitle="name"
      title="Aktienuniversum ändern"
      @submitSelectedItem="(val) => addShareUniverseToPortfolio(val)"
    />

  </div>
</template>

<script>
import {mapGetters} from "vuex"
import {finPortfolioAPI} from "@/js/finAPI"
import FinArchetypeAvatar from "@/components/common/ArchetypeAvatar"
import FinLexiconText from "@/components/common/lexicon/LexiconText"
import FinPortfolioValue from "@/components/pages/myPortfolio/PortfolioValue"
import FinChooseSelector from "@/components/common/ChooseSelector"
import AmplitudePush from "@/mixins/AmplitudePush"

export default {
  name: 'fin-strategy-and-universe-box',
  components: {FinChooseSelector, FinPortfolioValue, FinLexiconText, FinArchetypeAvatar},
  props: {
    strategy: {
      type: Object
    },
    portfolio: {
      type: Object
    },
    assetClass: {
      type: Object
    },
    isInactivePortfolio: {
      type: Boolean
    }
  },
  data() {
    return {
      showDialogChangeStrategy: false,
      showChangeShareUniverseDialog: false,
      shareUniverseName: this.portfolio?.shareUniverse?.name,
      selectedUniverse: {},
      universe: null
    }
  },
  computed: {
    ...mapGetters('auth', ['profileArchetype']),
  },
  mixins: [AmplitudePush],
  methods: {
    gotoStrategyDetails() {
      this.pushToAmplitude({
        'event': 'fincraft_portfolio_details_strategy_details',
        'step': 'show_strategy',
        'placement': 'fincraft_portfolio_details',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      const goTo = {
        name: this.assetClass.id === 'SHARE' ? 'finStrategy_details_portfolio' : 'finStrategy_etfdetails_portfolio',
        params: {
          stratId: this.strategy.id,
          portfolioId: this.portfolio.id,
          assetClassID: this.assetClass.id,
          goThenToPage: 'portfolioDetail',
          mode: 'showStrategy'
        }
      }
      this.$router.push(goTo)
    },
    showChooseStrategy() {
      this.pushToAmplitude({
        'event': 'fincraft_portfolio_details_choose_strategy',
        'step': 'choose_strategy',
        'placement': 'fincraft_portfolio_details',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      const goTo = {
        name: this.assetClass.id === 'SHARE' ? 'finStrategy_list_shares_portfolio' : 'finStrategy_list_etfs_portfolio',
        params: {
          mode: 'chooseStrategy',
          portfolioId: this.portfolio.id,
          assetClassID: this.assetClass.id,
          goThenToPage: 'portfolioDetail'
        }
      }
      this.$router.push(goTo)
    },
    changeStrategy() {
      this.pushToAmplitude({
        'event': 'fincraft_portfolio_details_change_strategy',
        'step': 'change_strategy',
        'placement': 'fincraft_portfolio_details',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.showDialogChangeStrategy = true
    },
    showDeleteStrategyConfirmDialog() {
      this.$confirm({
        title: 'Strategie für die Assetklasse ' + this.assetClass.name + ' löschen?',
        text: 'Möchtest Du dein Portfolio ohne Strategie für die Assetklasse ' + this.assetClass.name + ' fortfahren?',
        textWithLexikonLink: false,
        confirmBtn: 'Ok, löschen'
      })
        .then(() => {
          this.removeStrategy()
          this.showDialogChangeStrategy = false
        })
    },
    async removeStrategy() {
      console.log("removeStrategy this.$route.params", this.$route.params)
      if (this.portfolio.id) {
        try {
          // remove this strategy from the portfolio for this asset class
          console.log("async removing the strategy from the portfolio " + this.portfolio.id + " for the assetclass " + this.assetClass.id)
          if (this.assetClass.id === 'SHARE') {
            await finPortfolioAPI.updateSharePortfolioStrategy(this.portfolio.id, '')
            this.$set(this.portfolio, 'shareStrategy', null) // TODO better @input of list
          } else if (this.assetClass.id === 'SHARE_ETF') {
            await finPortfolioAPI.updateShareETFPortfolioStrategy(this.portfolio.id, '')
            this.$set(this.portfolio, 'shareETFStrategy', null) // TODO better @input of list
          } else if (this.assetClass.id === 'CORPORATE_BONDS_ETF') {
            await finPortfolioAPI.updateCorporateBondsETFPortfolioStrategy(this.portfolio.id, '')
            this.$set(this.portfolio, 'corporateBondsETFStrategy', null) // TODO better @input of list
          } else if (this.assetClass.id === 'GOVERNMENT_BONDS_ETF') {
            await finPortfolioAPI.updateGovernmentBondsETFPortfolioStrategy(this.portfolio.id, '')
            this.$set(this.portfolio, 'governmentBondsETFStrategy', null) // TODO better @input of list
          } else if (this.assetClass.id === 'COMMODITIES_ETF') {
            await finPortfolioAPI.updateCommoditiesETFPortfolioStrategy(this.portfolio.id, '')
            this.$set(this.portfolio, 'commoditiesETFStrategy', null) // TODO better @input of list
          }
          console.log("async removed the strategy from the portfolio " + this.portfolio.id + " for the assetclass " + this.assetClass.id)
          this.$toasted.global.finToast({
            message: 'Deine Strategie wurde erfolgreich entfernt.',
            type: 'success'
          })
        } catch (e) {
          console.error("removeStrategy: an error occurred", e)
          this.$toasted.global.finToast({
            message: 'Deine Strategie konnte nicht erfolgreich entfernt werden.',
            type: 'error'
          })
        }

        try {
          // deactivate monitoring in case the portfolio has no more strategy
          if (!this.portfolio.deactivateFinAlert && !this.portfolio.shareStrategy && !this.portfolio.shareETFStrategy
            && !this.portfolio.corporateBondsETFStrategy && !this.portfolio.governmentBondsETFStrategy
            && !this.portfolio.commoditiesETFStrategy) {
            // disable monitoring
            await finPortfolioAPI.updatePortfolioMonitoring(this.portfolio.id, false)
            this.$toasted.global.finToast({
              message: 'Da dein Portfolio keine Strategie mehr hat, wird dein Portfolio nicht mehr monitored.',
              type: 'info'
            })
          }
        } catch (e) {
          console.error("failed to deactivate the portfolio monitoring", e)
        }
      }
    },
    async loadUniverse() {
      if (this.assetClass.id === 'SHARE') {
        let res = await finPortfolioAPI.searchUniverses()
        this.selectedUniverse = this.portfolio.shareUniverse ? this.portfolio.shareUniverse : res[0]
        this.universe = res
        return res
      }
    },
    async addShareUniverseToPortfolio(selectedUniverse) {
      if (this.portfolio.id) {
        // save shareUniverse to portfolio
        await finPortfolioAPI.updateShareUniversePortfolio(this.portfolio.id, selectedUniverse)
        this.showChangeShareUniverseDialog = false
        this.selectedUniverse = this.universe.find(item => item.id === selectedUniverse)
        this.shareUniverseName = this.selectedUniverse.name
      }
    },
    async deleteShareUniverse() {
      if (this.portfolio.id) {
        // save shareUniverse to portfolio
        await finPortfolioAPI.updateShareUniversePortfolio(this.portfolio.id, null)
        this.selectedUniverse = null
        this.shareUniverseName = null
        // TODO fix unexpected mutation of prop
        this.portfolio.shareUniverse = null
      }
    }
  },
}

</script>

<style lang="scss" scoped>

.strategy-name {
  font-weight: bold;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
}

.universum {
  color: $fin-color-cool-grey;
  font-weight: normal;
  font-size: 12px;
}

.no-strategy {
  font-size: 14px;
  color: $fin-color-cool-grey;
  font-weight: normal;
}
</style>
